import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Button,
    IconButton,
    TextField,
    Autocomplete,
    Typography,
    Box,
    CircularProgress
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";

const ComparisonSelectedModal = ({ 
    open, 
    onClose, 
    selectedDonor, 
    donors, 
    onCompare 
}) => {
    const [searchTerm, setSearchTerm] = useState("");
    const [targetDonor, setTargetDonor] = useState(null);
    const [loading, setLoading] = useState(false);
    const [inputValue, setInputValue] = useState("");

    // Memoize available donors to prevent unnecessary recalculations
    const availableDonors = useMemo(() => {
        if (!donors || !selectedDonor) return [];
        return donors.filter(donor => donor._id !== selectedDonor._id);
    }, [donors, selectedDonor]);

    // Debounce search term updates
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            setSearchTerm(inputValue);
        }, 300);
        return () => clearTimeout(timeoutId);
    }, [inputValue]);

    // Memoize filtered donors based on search term
    const filteredDonors = useMemo(() => {
        if (!searchTerm || searchTerm.length < 2) {
            return availableDonors.slice(0, 100); // Limit initial results for performance
        }

        const searchValue = searchTerm.toLowerCase();
        const filtered = availableDonors.filter(donor => {
            // Safe string conversion and search
            const donorId = donor.donorId ? String(donor.donorId).toLowerCase() : '';
            const firstName = donor.firstName ? donor.firstName.toLowerCase() : '';
            const lastName = donor.lastName ? donor.lastName.toLowerCase() : '';
            const mobile1 = donor.mobile1 ? String(donor.mobile1) : '';
            const mobile2 = donor.mobile2 ? String(donor.mobile2) : '';
            const mailId = donor.mailId ? donor.mailId.toLowerCase() : '';
            
            return (
                donorId.includes(searchValue) ||
                firstName.includes(searchValue) ||
                lastName.includes(searchValue) ||
                mobile1.includes(searchTerm) ||
                mobile2.includes(searchTerm) ||
                mailId.includes(searchValue)
            );
        });
        
        return filtered.slice(0, 50); // Limit search results for performance
    }, [availableDonors, searchTerm]);

    const handleInputChange = useCallback((event, value) => {
        setInputValue(value || '');
    }, []);

    const handleCompare = useCallback(() => {
        if (targetDonor && selectedDonor) {
            setLoading(true);
            onCompare(selectedDonor, targetDonor);
            setLoading(false);
        }
    }, [targetDonor, selectedDonor, onCompare]);

    const handleClose = useCallback(() => {
        setSearchTerm("");
        setInputValue("");
        setTargetDonor(null);
        onClose();
    }, [onClose]);

    const getOptionLabel = useCallback((option) => {
        if (!option) return '';
        const donorId = option.donorId || 'N/A';
        const firstName = option.firstName || '';
        const lastName = option.lastName || '';
        const mobile = option.mobile1 || 'No mobile';
        return `${donorId} - ${firstName} ${lastName} (${mobile})`;
    }, []);

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
            <div className="modal-header" style={{ 
                background: "#1976D2", 
                color: "white", 
                fontWeight: "bold", 
                display: "flex", 
                justifyContent: "space-between", 
                alignItems: "center", 
                padding: "16px", 
                borderBottom: "1px solid #ddd" 
            }}>
                <DialogTitle style={{ color: "white", padding: 0 }}>
                    Select Donor to Compare
                </DialogTitle>
                <IconButton onClick={handleClose} data-log="Close Icon Button">
                    <CloseIcon sx={{ color: "white" }} />
                </IconButton>
            </div>

            <div className="modal-body" style={{ padding: "24px" }}>
                <DialogContent style={{ padding: 0 }}>
                    {selectedDonor && (
                        <Box mb={3}>
                            <Typography variant="h6" gutterBottom>
                                Selected Donor (Source):
                            </Typography>
                            <Box p={2} bgcolor="#f5f5f5" borderRadius={1}>
                                <Typography>
                                    <strong>ID:</strong> {selectedDonor.donorId} | 
                                    <strong> Name:</strong> {selectedDonor.firstName} {selectedDonor.lastName} | 
                                    <strong> Mobile:</strong> {selectedDonor.mobile1 || '-'}
                                </Typography>
                            </Box>
                        </Box>
                    )}

                    <Typography variant="h6" gutterBottom>
                        Search and Select Target Donor:
                    </Typography>
                    
                    <Autocomplete
                        options={filteredDonors}
                        getOptionLabel={getOptionLabel}
                        value={targetDonor}
                        onChange={(event, newValue) => setTargetDonor(newValue)}
                        onInputChange={handleInputChange}
                        inputValue={inputValue}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Search by Donor ID, Name, Mobile, or Email"
                                variant="outlined"
                                fullWidth
                                InputProps={{
                                    ...params.InputProps,
                                    startAdornment: <SearchIcon style={{ marginRight: 8, color: '#666' }} />,
                                }}
                            />
                        )}
                        renderOption={(props, option) => (
                            <li {...props} key={option._id}>
                                <Box>
                                    <Typography variant="body1">
                                        <strong>{option.donorId}</strong> - {option.firstName} {option.lastName}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        Mobile: {option.mobile1 || '-'} | Email: {option.mailId || '-'}
                                    </Typography>
                                </Box>
                            </li>
                        )}
                        noOptionsText={searchTerm.length < 2 ? "Type at least 2 characters to search" : "No donors found. Try different search terms."}
                        loading={searchTerm !== inputValue}
                        loadingText="Searching..."
                    />

                    {targetDonor && (
                        <Box mt={3}>
                            <Typography variant="h6" gutterBottom>
                                Target Donor Selected:
                            </Typography>
                            <Box p={2} bgcolor="#e3f2fd" borderRadius={1}>
                                <Typography>
                                    <strong>ID:</strong> {targetDonor.donorId} | 
                                    <strong> Name:</strong> {targetDonor.firstName} {targetDonor.lastName} | 
                                    <strong> Mobile:</strong> {targetDonor.mobile1 || '-'}
                                </Typography>
                            </Box>
                        </Box>
                    )}
                </DialogContent>
            </div>

            <div className="modal-footer" style={{ 
                display: "flex", 
                justifyContent: "flex-end", 
                padding: "16px", 
                borderTop: "1px solid #ddd" 
            }}>
                <DialogActions>
                    <Button 
                        onClick={handleCompare} 
                        color="primary" 
                        variant="contained"
                        disabled={!targetDonor || loading}
                        startIcon={loading ? <CircularProgress size={20} /> : null}
                    >
                        {loading ? 'Processing...' : 'Compare'}
                    </Button>
                    <Button 
                        onClick={handleClose} 
                        color="error" 
                        variant="contained" 
                        style={{ marginLeft: "8px" }}
                        disabled={loading}
                    >
                        Cancel
                    </Button>
                </DialogActions>
            </div>
        </Dialog>
    );
};

export default ComparisonSelectedModal;
